import React from "react";
import { Box, Paper, Typography } from "@mui/material";

const AdminStatCard = ({
  label,
  value,
  note,
  icon,
  color = "#475569",
  background = "#f8fafc",
  selected = false,
  onClick,
  delay = 0,
}) => (
  <Paper
    component={onClick ? "button" : "div"}
    type={onClick ? "button" : undefined}
    elevation={0}
    onClick={onClick}
    sx={{
      position: "relative",
      display: "flex",
      flexDirection: "column",
      width: "100%",
      minHeight: { xs: 124, sm: 140, md: 168 },
      overflow: "hidden",
      p: { xs: 1.25, sm: 1.75, md: 2.25 },
      border: `1px solid ${selected ? color : `${color}35`}`,
      borderRadius: "8px",
      bgcolor: background,
      color: "#1e293b",
      font: "inherit",
      textAlign: "left",
      cursor: onClick ? "pointer" : "default",
      boxShadow: selected ? `0 0 0 2px ${color}18, 0 10px 24px ${color}14` : `0 4px 14px ${color}0d`,
      animation: "adminStatCardIn 480ms cubic-bezier(0.16, 1, 0.3, 1) backwards",
      animationDelay: `${delay}ms`,
      transition: "border-color .18s ease, box-shadow .18s ease, transform .18s ease",
      "&::before": {
        content: '""',
        position: "absolute",
        inset: "0 0 auto",
        height: 3,
        bgcolor: color,
      },
      "&:hover": onClick ? {
        borderColor: color,
        boxShadow: `0 12px 28px ${color}1c`,
        transform: "translateY(-3px)",
      } : undefined,
      "&:focus-visible": {
        outline: `3px solid ${color}42`,
        outlineOffset: 3,
      },
      "@keyframes adminStatCardIn": {
        from: { opacity: 0, transform: "translateY(10px)" },
        to: { opacity: 1, transform: "translateY(0)" },
      },
      "@media (prefers-reduced-motion: reduce)": {
        animation: "none",
        transition: "none",
      },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: { xs: 0.75, sm: 1.5 } }}>
      <Typography
        variant="overline"
        sx={{ pt: 0.25, color, fontSize: { xs: 9, sm: 10 }, fontWeight: 800, letterSpacing: ".5px", lineHeight: 1.35 }}
      >
        {label}
      </Typography>
      <Box
        aria-hidden="true"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: { xs: 28, sm: 34, md: 38 },
          height: { xs: 28, sm: 34, md: 38 },
          flexShrink: 0,
          borderRadius: { xs: "6px", sm: "8px" },
          bgcolor: `${color}14`,
          color,
          "& .MuiSvgIcon-root": { fontSize: { xs: 16, sm: 18, md: 20 } },
        }}
      >
        {icon}
      </Box>
    </Box>
    <Typography
      variant="h4"
      sx={{
        my: { xs: 0.75, sm: 1.25, md: 1.5 },
        color: "#1e293b",
        fontWeight: 800,
        fontSize: { xs: "1.2rem", sm: "1.5rem", md: "2.125rem" },
        lineHeight: 1.2,
        overflowWrap: "anywhere",
      }}
    >
      {value}
    </Typography>
    <Typography
      variant="caption"
      sx={{
        mt: "auto",
        pt: { xs: 0.75, sm: 1.25 },
        borderTop: `1px solid ${color}20`,
        color: "#64748b",
        fontSize: { xs: "0.68rem", sm: "0.75rem" },
        lineHeight: 1.35,
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      }}
    >
      {note}
    </Typography>
  </Paper>
);

export default AdminStatCard;
